import { useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import type { Project, ProjectImage } from '@/mocks/projects';
import { buildWavePath, measurePathLength } from './hero-path-geometry';
import { stepHeroSpeed, type HeroSpeed } from './hero-speed';
import { isShutterRunning } from '@/lib/shutter/shutter-controller';
import { isHeroVisible } from '@/lib/shutter/hero-layer-store';

export interface HeroStreamImage {
  image: ProjectImage;
  project: Project;
}

interface Props {
  stageRef: RefObject<HTMLDivElement | null>;
  pool: HeroStreamImage[];
  /** Vertical centre of the wave as a fraction of stage height. */
  midRatio: number;
  amplitude: number;
  /** Wavelength as a fraction of stage width (≈0.4 → ~2.5 crests across). */
  wavelengthRatio: number;
  phase: number;
  /** Seconds per full cycle. */
  duration: number;
  direction: 1 | -1;
  /** Offset into the pool so the two tracks don't show the same sequence. */
  poolOffset: number;
  /** Starting index into the wave variation pattern. */
  seed: number;
  label: string;
  /** Shared speed multiplier (both tracks). */
  speed: RefObject<HeroSpeed>;
  hoveredId: string | null;
  /** Nearest card in this track's magnification field (null = none). */
  onActiveChange: (label: string, id: string | null) => void;
  /** Mobile: smaller cards, tighter wave, no hover field. */
  compact?: boolean;
}

// Dock-style proximity field (per track, horizontal screen-space).
const MAG_MAX = 1.3;
const MAG_RADIUS = 380; // px each side (~2.5 card pitches)
const PUSH_MAX = 22; // px outward shell translation
const SMOOTH_TAU = 80; // ms; exponential smoothing → ~250ms settle
const ACTIVE_MIN = 0.35; // influence needed to become the active project

// One strict 4:3 card for every Hero item; images crop inside via object-fit: cover.
const CARD_W = 96;
const CARD_H = 128;
const GAP = 24; // visual gap between neighbours (px of arc length)
const OVERSCAN = 220; // > widest image, so wrap happens off-screen

/**
 * HeroPathTrack — one infinite SVG-path marquee.
 * Items sit on CSS offset-path; one global progress value (0–100) drives
 * every item's offset-distance = (base_i + progress) mod 100 via rAF.
 */
export default function HeroPathTrack({ stageRef, pool, midRatio, amplitude, wavelengthRatio, phase, duration, direction, poolOffset, seed, label, speed, hoveredId, onActiveChange, compact = false }: Props) {
  const cardW = compact ? 72 : CARD_W;
  const cardH = compact ? 96 : CARD_H;
  const gap = compact ? 14 : GAP;
  const [geo, setGeo] = useState<{ d: string; length: number } | null>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const pointerX = useRef<number | null>(null);

  // Track-owned pointer band: only pointers near THIS wave engage its field.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || compact) { pointerX.current = null; return undefined; }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      const r = stage.getBoundingClientRect();
      const mid = r.top + r.height * midRatio;
      const band = amplitude * 1.4 + CARD_H;
      pointerX.current = Math.abs(e.clientY - mid) <= band ? e.clientX : null;
    };
    const off = () => { pointerX.current = null; };
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerleave', off);
    window.addEventListener('blur', off);
    return () => {
      stage.removeEventListener('pointermove', onMove);
      stage.removeEventListener('pointerleave', off);
      window.removeEventListener('blur', off);
    };
  }, [stageRef, midRatio, amplitude, compact]);

  // Geometry from container size (rebuilt on resize; progress is kept).
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    let raf = 0;
    const build = () => {
      const { width, height } = stage.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      const d = buildWavePath({ width, midY: height * midRatio, amplitude, wavelength: Math.max(compact ? 260 : 560, width * wavelengthRatio), phase, overscan: OVERSCAN, seed });
      setGeo({ d, length: measurePathLength(d) });
    };
    build();
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(build);
    });
    ro.observe(stage);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, [stageRef, midRatio, amplitude, wavelengthRatio, phase, seed]);

  // Fill the path with items using a consistent mean arc-length step.
  const items = useMemo(() => {
    if (!geo || pool.length === 0) return [];
    const count = Math.max(4, Math.floor(geo.length / (cardW + gap)));
    return Array.from({ length: count }, (_, i) => pool[(i + poolOffset) % pool.length]);
  }, [geo, pool, poolOffset, cardW, gap]);

  // Continuous motion: one progress value, per-item offset, wrap 0–100.
  useEffect(() => {
    if (!items.length) return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const n = items.length;
    let progress = 0;
    let last = performance.now();
    let raf = 0;
    const scales = new Float32Array(n).fill(1);
    const shifts = new Float32Array(n);
    let activeIdx = -1;
    const magMax = reduced ? 1.04 : MAG_MAX;
    const pushMax = reduced ? 0 : PUSH_MAX;
    const field = (dtMs: number) => {
      const px = pointerX.current;
      const k = 1 - Math.exp(-dtMs / SMOOTH_TAU);
      let best = -1;
      let bestInf = ACTIVE_MIN;
      for (let i = 0; i < n; i += 1) {
        const el = itemRefs.current[i];
        const shell = el?.firstElementChild as HTMLElement | null;
        if (el && shell) {
          let inf = 0;
          let push = 0;
          if (px !== null) {
            const r = el.getBoundingClientRect(); // path item: untransformed by shell
            const t = (r.left + r.width / 2 - px) / MAG_RADIUS;
            if (t > -1 && t < 1) {
              inf = (Math.cos(t * Math.PI) + 1) / 2; // cosine falloff 1 → 0
              push = Math.sin(t * Math.PI); // outward, 0 at centre & edge
            }
          }
          if (inf > bestInf) { bestInf = inf; best = i; }
          scales[i] += (1 + (magMax - 1) * inf - scales[i]) * k;
          shifts[i] += (pushMax * push - shifts[i]) * k;
          const s = scales[i];
          const idle = Math.abs(s - 1) < 0.001 && Math.abs(shifts[i]) < 0.05;
          shell.style.transform = idle ? '' : `translate3d(${shifts[i].toFixed(2)}px, 0, 0) scale(${s.toFixed(4)})`;
          el.style.zIndex = idle ? '' : String(10 + Math.round((s - 1) / (MAG_MAX - 1) * 100) + (i === best ? 20 : 0));
        }
      }
      if (best !== activeIdx) {
        activeIdx = best;
        onActiveChange(label, best >= 0 ? `${label}-${best}` : null);
      }
    };
    const apply = () => {
      for (let i = 0; i < n; i += 1) {
        const el = itemRefs.current[i];
        if (el) {
          const v = (((i / n) * 100 + progress) % 100 + 100) % 100;
          el.style.offsetDistance = `${v}%`;
        }
      }
    };
    const tick = (now: number) => {
      const dt = Math.min(now - last, 100) / 1000; // clamp tab-switch jumps
      last = now;
      // Frozen during the shutter transition and while the Hero layer is hidden.
      if (isShutterRunning() || !isHeroVisible()) { raf = requestAnimationFrame(tick); return; }
      if (!reduced) {
        const mult = speed.current ? stepHeroSpeed(speed.current, now) : 1;
        progress = (progress + direction * mult * (dt / duration) * 100 + 100) % 100;
        apply();
      }
      field(dt * 1000);
      raf = requestAnimationFrame(tick);
    };
    apply();
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); onActiveChange(label, null); };
  }, [items, duration, direction, speed, onActiveChange, label]);

  return (
    <div className="hero-path-track pointer-events-none absolute inset-0" aria-label={label}>
      {geo && items.map((it, i) => {
        const id = `${label}-${i}`;
        const active = !compact && hoveredId === id;
        return (
          <figure
            key={`${it.image.src}-${i}`}
            ref={(el) => { itemRefs.current[i] = el; }}
            data-hero-instance={id}
            className={`hero-path-item pointer-events-auto m-0 cursor-default select-none${active ? ' is-hovered' : ''}`}
            style={{ offsetPath: `path('${geo.d}')`, width: cardW, height: cardH, aspectRatio: '3 / 4' } as CSSProperties}
          >
            <div className={`hero-image-shell relative h-full w-full${active ? ' is-active' : ''}`}>
              <div className="h-full w-full overflow-hidden bg-background-200">
                <img
                  src={it.image.src}
                  alt={it.image.alt}
                  title={`${it.project.title} — ${it.project.category} photography`}
                  style={{ objectPosition: it.image.focal }}
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              </div>
              <div className="hero-meta" aria-hidden="true">
                <span className="hero-meta__title">{it.project.title}</span>
                <span className="hero-meta__sub">{it.project.category} · {it.project.year}</span>
              </div>
            </div>
          </figure>
        );
      })}
    </div>
  );
}